// libs
import React, { lazy } from "react";
import { Router } from "@remix-run/router";
import { RouteObject, createBrowserRouter, matchRoutes } from "react-router-dom";

// components
import PageContainer from "./components/navigation/PageContainer";
import { RouteErrorBoundary } from "@components/RouteErrorBoundary";

// utils
import { configureAmplify, fetchCredentials } from "@utils/appsync";
import { LicenseManager } from "@amzn/gft-ag-grid-enterprise";

// services
import { SecretsService } from "@services/SecretsService";
import { LoggerService } from "@services/LoggerService";
import { MetricsService } from "@services/MetricsService";
import { rumService } from "@services/rumService";
import { SettingsService } from "@services/SettingsService";
import { RUM_EVENT_TYPES, RUM_ACTIONS, RUM_COMPONENTS } from "@services/rumEventHelpers";

// constants
import { METRICS_NAMESPACE } from "@constants/index";

// providers
import { NewSimulationProvider } from "@store/context/NewSimulation";

// screens
const Home = lazy(() => import("@pages/Home"));
const AllocationConfig = lazy(() => import("@pages/AllocationConfig"));
const NewSimulation = lazy(() => import("@pages/simulations/NewSimulation"));
const SimulationResult = lazy(() => import("@pages/simulations/SimulationResult"));
const SimulationHistory = lazy(() => import("@pages/simulations/SimulationHistory"));
const SharedSimulations = lazy(() => import("@pages/simulations/SharedSimulations"));
const CostPools = lazy(() => import("@pages/CostPools"));


export const ALLOCATION_TYPE_PARAM = "allocationType";
export const SANDBOX_ID_PARAM = "sandboxId";

export const SIMULATIONS_ROUTES = [{ path: "/simulations" }, { path: "/simulations/shared" }];

export const SIMULATION_ROUTES = [
    { path: "/simulation/:resourceKey" },
    { path: "/simulation/result/:resourceKey" },
    { path: "/simulation/result/shared/:resourceKey" },
];

export const DICTIONARY_ROUTES = [{ path: "/dictionary" }, { path: "/simulation/:inputType" }];


// Helper function to match multiple routes
export const isCurrentRouteMatching = (currentRoute: string, routes: Array<RouteObject>): boolean => {
    if (matchRoutes(routes, currentRoute)) {
        return true;
    }
    return false;
};

export const getParamFromRouteQuery = (routeQueryString: string, paramName: string): string => {
    if (!routeQueryString) {
        return "";
    }
    const urlSearchParams = new URLSearchParams(routeQueryString);
    return urlSearchParams.get(paramName);
};

let appLaunchEventRecorded = false;

export const getRouter = (): Router => {
    const setupLoader = async (): Promise<unknown> => {
        // eslint-disable-next-line no-undef
        const a3sSettings = await SettingsService.loadSettings();

        const cognitoInfo = await configureAmplify(a3sSettings);

        // AG-Grid Enterprise license fetch from Secrets Manager
        try {
            const cognito = a3sSettings?.cognito;
            const secretName = a3sSettings?.secretName;
            const idToken = cognitoInfo?.getSignInUserSession()?.getIdToken()?.getJwtToken();

            if (cognito && secretName && idToken) {
                SecretsService.initialize(cognito.region, cognito.identityPoolId, idToken, cognito.userPoolId);
                const secrets = await SecretsService.getSecrets(secretName);
                const key = secrets["ag-grid-enterprise-key"] as string;
                if (key) LicenseManager.setLicenseKey(key);
            } else {
                // Dev fallback: read from .env.local
                const envKey = import.meta.env.VITE_AG_GRID_LICENSE_KEY as string | undefined;
                if (envKey) LicenseManager.setLicenseKey(envKey);
            }
        } catch (error) {
            const envKey = import.meta.env.VITE_AG_GRID_LICENSE_KEY as string | undefined;
            if (envKey) {
                LicenseManager.setLicenseKey(envKey);
            } else if (!import.meta.env.DEV) {
                console.error("[ag-grid-license] Failed to fetch license:", error);
            }
        }

        try {
            const rumInitPromise = a3sSettings?.rum
                ? rumService.initialize(SettingsService.getRumConfig(a3sSettings)).catch((err) => {
                      console.error("RUM initialization failed:", err);
                  })
                : Promise.resolve();

            await Promise.all([
                // initialising the logger.
                LoggerService.getLoggerServiceInstance().initialiseLogger({
                    fetchCredentials: fetchCredentials(),
                    region: a3sSettings?.cloudwatch.region,
                    logGroupName: a3sSettings?.cloudwatch.logGroupName,
                    userId: (cognitoInfo.getUsername() || "").replace(a3sSettings.cognito.providerName + "_", ""),
                }),
                // initialising the Metrics.
                MetricsService.getMetricsServiceInstance().initialiseMetrics({
                    namespace: METRICS_NAMESPACE,
                    region: a3sSettings?.cloudwatch.region,
                    fetchCredentials: fetchCredentials(),
                }),
                // initialising RUM.
                rumInitPromise,
            ]);

            // Record app launch event after RUM is initialized
            try {
                if (a3sSettings?.rum && rumService.isRumInitialized() && !appLaunchEventRecorded) {
                    const username = (cognitoInfo.getUsername() || "Guest User").replace(a3sSettings.cognito.providerName + "_", "");
                    rumService.recordBusinessEvent(
                        RUM_EVENT_TYPES.APPLICATION_LAUNCHED,
                        RUM_ACTIONS.LAUNCH,
                        RUM_COMPONENTS.APPLICATION,
                        username,
                    );
                    appLaunchEventRecorded = true;
                } else if (appLaunchEventRecorded) {
                    console.log("App launch event already recorded, skipping duplicate");
                }
            } catch (eventError) {
                console.error("Failed to record app launch event:", eventError);
            }
        } catch (errors) {
            // failed to initialise the logger and metrics
        }

        return {
            settingsData: a3sSettings,
            cognitoInfo,
        };
    };

    return createBrowserRouter([
        {
            element: <PageContainer />,
            errorElement: <RouteErrorBoundary />,
            loader: setupLoader,
            children: [
                {
                    path: "/",
                    element: <Home />,
                },
                {
                    path: "/simulations",
                    element: <SimulationHistory />,
                },
                {
                    path: "/simulation",
                    children: [
                        {
                            path: "",
                            element: (
                                <NewSimulationProvider>
                                    <NewSimulation />
                                </NewSimulationProvider>
                            ),
                        },
                        {
                            path: "result/:resourceKey",
                            element: (
                                <NewSimulationProvider>
                                    <SimulationResult />
                                </NewSimulationProvider>
                            ),
                        },
                        {
                            path: ":resourceKey",
                            element: (
                                <NewSimulationProvider>
                                    <NewSimulation />
                                </NewSimulationProvider>
                            ),
                        },
                        {
                            path: "result/shared/:resourceKey",
                            element: (
                                <NewSimulationProvider>
                                    <SimulationResult />
                                </NewSimulationProvider>
                            ),
                        },
                    ],
                },
                {
                    path: "/simulations/shared",
                    element: <SharedSimulations />,
                },
                {
                    path: "/dictionary",
                    children: [
                        {
                            path: "",
                            element: <AllocationConfig />,
                        },
                        {
                            path: ":inputType",
                            element: <AllocationConfig />,
                        },
                    ],
                },
                {
                    path: "/costpools",
                    element: <CostPools />,
                },
            ],
        },
    ]);
};
